import { useMutation } from '@tanstack/react-query';
import { FolderInput } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import axios from 'src/axios/axios';
import { useMS } from 'src/components/files/onedrive/context/MSContext';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "src/components/ui/alert-dialog";
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Combobox } from 'src/components/ui/combobox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { Switch } from 'src/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { deleteItemFromOneDrive, getFileContentAsBase64, moveToTrash } from 'src/graph';
import { useAppSelector } from 'src/hooks/redux';
import useAuth from 'src/hooks/useAuth';
import { getWindevLang } from 'src/lib/lang';
import { RootState } from 'src/store';

type P = {
    selected_documents: string[]
    documents?: any
    doctypeCollection?: any
    onSuccess?: () => void
}

const UploadToFitdoc = ({selected_documents, documents, doctypeCollection, onSuccess}:P) => {
    const { auth } = useAuth()
    const { accessToken } = useMS()
    const { i18n } = useTranslation()

    const doctypes:DoctypeObject[] = useAppSelector((state:RootState)=>state.doctypes);
    const metalist:MetatypeObject[] = useAppSelector((state:RootState)=>state.metatypes);
    const dossiers:DossierObject[] = useAppSelector((state:RootState) => state.dossiers);

    const [open, setopen] = useState<boolean>(false);
    const [metatypes, setmetatypes] = useState<DoctypeMetatypes[]>([]);

    const [selected_metatypes, setselected_metatypes] = useState<{metatype:string, metavalue:string}[]>();
    const [selected_dossier, setselected_dossier] = useState<string | undefined>();
    const [removeFromDrive, setremoveFromDrive] = useState(true);

    const [filter_dossiers_mine, setfilter_dossiers_mine] = useState(true);

    useEffect(()=>{
        if(!open) return;
        if(!doctypeCollection.doctype){ toast.error('Geen documenttype gevonden', {id:'utf'}); setopen(false); return; }
        if(!Array.isArray(doctypes)){ toast.error('Geen FitDoc documenttypes ingeladen', {id:'utf'}); setopen(false); return; }
        const dt = doctypes.find(i=>i.doctype===doctypeCollection.doctype)
        if(!dt){ toast.error('Geen documenttype gevonden', {id:'utf'}); setopen(false); return; }
        
        setmetatypes(dt.metatypes)
    }, [open])

    function removeNonAlphanumeric(str:string) {
        return str.replace(/[^a-zA-Z0-9 ]/g, '');
    }

    const { mutate:uploadToFitdoc } = useMutation({
        mutationFn: async () => {
            try {
                toast.loading('Documenten uploaden in FITdoc...', { id:'updateDocuments' });
    
                const updatePromises = selected_documents.map(async (id) => {
                    const fileOneDrive = documents.find((d:any) => d.id === id);
                    const fileNameOneDrive = fileOneDrive?.name || '';
                    //const startIndex = fileNameOneDrive.indexOf('] - ') + 4; // +4 to include the ' - ' part
                    const fileName = fileNameOneDrive

                    const base64String = await getFileContentAsBase64(accessToken, fileOneDrive.id);

                    let body:any = {
                        token: auth.data?.token,
                        filename: fileName,
                        file: base64String,
                        doctype: doctypeCollection.doctype,
                    }
                    if(selected_metatypes && selected_metatypes?.length > 0){ body.metatypes = selected_metatypes }
                    if(selected_dossier){ body.dossiers = [{dossierentityid: selected_dossier}] }
    
                    const response = await axios.post(`documents/add`, body);
    
                    if (response.data.errorcode !== 0) {
                        throw new Error(`Document ${id} adding to fitdoc failed`);
                    }
                    
                    if(removeFromDrive){
                        try {
                            await deleteItemFromOneDrive(accessToken, fileOneDrive.id);
                        } catch (e:any) {
                            if(e.message === 'The resource you are attempting to access is locked'){
                                console.log('Bestand gelocked, hoogstwaarschijnlijk nog open');
                            }
                            
                            console.log("Document removing from ONEDRIVE failed, move to trash map to remove later")
                            await moveToTrash(accessToken, fileOneDrive.id)
                        }
                    }
                    
                });
    
                await Promise.all(updatePromises);
    
                onSuccess && onSuccess();
                setopen(false);
                toast.remove('updateDocuments');
            } catch (error) {
                console.log(error);
                toast.error('Document updaten in FitDoc mislukt', { id:'updateDocuments' })
            }
        }
    })

    function countDossierFilters () {
        let count = 0;
        if(filter_dossiers_mine){ count++ }
        return count
    }

    function createDossierOptions () {
        let d = Array.isArray(dossiers) ? dossiers : []
        if(filter_dossiers_mine){ d = d.filter(i=>i.owner === auth.data?.user.fitdocusername) }
        
        let cbList = d.map(d=>({value:d.dossierentityid, label:removeNonAlphanumeric(d.extendeddescription)})).filter(i => i.label.length > 0)
        return cbList;
    }

    let dossiersOptions = createDossierOptions()

  return (
    <AlertDialog open={selected_documents.length < 1 ? false : open} onOpenChange={(o)=>{setopen(o)}}>
        <AlertDialogTrigger asChild>
            <div>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild><button disabled={selected_documents.length < 1} className='w-8 h-8 rounded-sm flex items-center justify-center border border-solid border-transparent hover:border-neutral-200 hover:bg-neutral-100 disabled:opacity-30'><FolderInput className='w-4 h-4 text-blue-600'/></button></TooltipTrigger>
                        <TooltipContent><p>Documenten uploaden in FITdoc</p></TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </AlertDialogTrigger>
        <AlertDialogContent overlayClassName='bg-black/60' className='p-0 flex max-h-[calc(100%-32px)] max-w-[1080px] min-w-[288px] min-h-[176px] w-full h-[680px] mx-8 gap-0 overflow-hidden'>
            <div className='border-r w-56 min-w-56 flex flex-col bg-slate-50'>
                <div className='p-4'>
                    <p className='font-semibold text-lg whitespace-nowrap'>{selected_documents.length} items uploaden</p>
                </div>
                <div className='overflow-y-auto flex flex-col flex-auto'>
                    <div className='flex-auto px-4'>
                        <div className='flex flex-col gap-1'>
                            {selected_documents.map((document,i)=>(
                                <div className='px-2 py-1 rounded-sm bg-slate-100 overflow-hidden' key={`utf-${i}`}>
                                    <p className='line-clamp-1 text-sm'>{Array.isArray(documents) ? (documents.find(d=>d.id===document)?.name || document) : document}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className='w-full flex flex-col'>
                <div className='h-full border-b overflow-auto'>
                    <div className='p-4 flex flex-col gap-4'>
                        {/* {metatypes.map((metatype,i)=>(
                            <div key={`utf-mt-${i}`}>
                                <Label className='flex items-center mb-1'>{metatypeToName(metatype.metatype)}{metatype.required === 255 && <span className='ml-1 text-red-600'>*</span>}</Label>
                                <Input />
                            </div>
                        ))} */}
                        <div>
                            <div className='flex items-end justify-between mb-1'>
                                <Label>Dossier</Label>
                                <Popover>
                                    <PopoverTrigger asChild><button className='text-sm text-primary font-medium bg-primary/10 rounded-sm px-2'>Filters ({countDossierFilters()})</button></PopoverTrigger>
                                    <PopoverContent>
                                        <div className='grid grid-cols-[20px,1fr] gap-4'>
                                            <div className='flex items-center'><Checkbox checked={filter_dossiers_mine} onCheckedChange={(c)=>{setfilter_dossiers_mine(c as boolean)}} id='my-files'/></div>
                                            <div className='flex items-center'><Label htmlFor='my-files'>Mijn dossiers</Label></div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                                
                            </div>
                            <Combobox selectedValue={selected_dossier?.toString()} onValueChange={(v)=>{setselected_dossier(v.value.toString())}} options={dossiersOptions} placeholder='Zoeken...'/>
                        </div>
                    </div>
                </div>
                <div className='flex items-center justify-between gap-2 p-4'>
                    <div>
                        <div className='flex items-center gap-2'>
                            <Switch checked={removeFromDrive} onCheckedChange={(c)=>{setremoveFromDrive(c)}} id='remove-from-onedrive'/>
                            <Label htmlFor='remove-from-onedrive'>Verwijderen uit OneDrive</Label>
                        </div>
                    </div>
                    <div className='flex items-center gap-2'>
                        <AlertDialogCancel asChild className='h-9'><Button size='sm' variant='outline'>Annuleren</Button></AlertDialogCancel>
                        <Button onClick={()=>{uploadToFitdoc()}} size='sm'>Uploaden</Button>
                    </div>
                </div>
            </div>
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default UploadToFitdoc